/**
 * VisualPecaFita — desenho READ-ONLY da peça sinalizando onde há fita de
 * borda + COTAS (largura no topo, comprimento à direita, em mm). Reusa a
 * REGRA DO VEIO via LADOS_PECA (comprimento no eixo vertical; verticais =
 * Frente/Traseira, horizontais = LE/LD). Sem interação — usado no carrossel
 * do detalhe (Gestão de Solicitações) para o gestor conferir.
 *
 * Props:
 * - `peca`: { comprimento_mm, largura_mm, fita_frente, fita_traseira,
 *             fita_le, fita_ld } (aceita string ou número nas medidas)
 */
import React from "react";

import { LADOS_PECA, parseDecimal } from "./utils";

const ALT_MAX = 150;
const ALT_MIN = 70;
const LARG_MAX = 170;
const LARG_MIN = 44;

// Margens assimétricas: sobra no topo (cota de largura) e à direita (cota de
// comprimento), depois das etiquetas das bordas.
const MARGEM_ESQ = 30;
const MARGEM_DIR = 68;
const MARGEM_TOPO = 52;
const MARGEM_BASE = 30;

function dimensoes(c, l) {
  let alt = ALT_MAX;
  let larg = alt * (l / c);
  if (larg > LARG_MAX) {
    larg = LARG_MAX;
    alt = larg * (c / l);
  }
  if (alt < ALT_MIN) alt = ALT_MIN;
  if (larg < LARG_MIN) larg = LARG_MIN;
  return { alt: Math.round(alt), larg: Math.round(larg) };
}

/** "1000.0" → "1000" ; "560.5" → "560,5". */
function fmtMedida(v) {
  const n = parseDecimal(v);
  if (n == null) return "";
  return Number.isInteger(n) ? String(n) : n.toLocaleString("pt-BR");
}

export default function VisualPecaFita({ peca }) {
  const c = parseDecimal(peca?.comprimento_mm);
  const l = parseDecimal(peca?.largura_mm);

  if (!c || !l || c <= 0 || l <= 0) {
    return (
      <div style={{ color: "var(--ink-2)", fontSize: 13, textAlign: "center", padding: "18px 0" }}>
        Sem medidas — não há desenho da peça.
      </div>
    );
  }

  const { alt, larg } = dimensoes(c, l);
  const vw = MARGEM_ESQ + larg + MARGEM_DIR;
  const vh = MARGEM_TOPO + alt + MARGEM_BASE;
  const x0 = MARGEM_ESQ;
  const y0 = MARGEM_TOPO;
  const x1 = x0 + larg;
  const y1 = y0 + alt;
  const cx = x0 + larg / 2;
  const cy = y0 + alt / 2;

  // Cotas (linhas de medida) — neutras (--ink-2), fora das etiquetas.
  const yCota = y0 - 30; // largura: acima do rótulo do topo
  const xCota = x1 + 46; // comprimento: à direita do rótulo lateral

  const geomPorLado = {
    topo: { linha: { x1: x0, y1: y0, x2: x1, y2: y0 }, texto: { x: cx, y: y0 - 11, rot: 0 } },
    base: { linha: { x1: x0, y1, x2: x1, y2: y1 }, texto: { x: cx, y: y1 + 18, rot: 0 } },
    esq: { linha: { x1: x0, y1: y0, x2: x0, y2: y1 }, texto: { x: x0 - 13, y: cy, rot: -90 } },
    dir: { linha: { x1, y1: y0, x2: x1, y2: y1 }, texto: { x: x1 + 13, y: cy, rot: 90 } },
  };

  const hachuras = [];
  for (let hx = x0 + 10; hx < x1 - 3; hx += 13) hachuras.push(hx);

  return (
    <svg
      viewBox={`0 0 ${vw} ${vh}`}
      width="100%"
      style={{ maxWidth: vw, display: "block", margin: "0 auto" }}
      role="img"
      aria-label={`Desenho da peça: comprimento ${fmtMedida(c)} mm por largura ${fmtMedida(l)} mm, com as bordas que têm fita`}
    >
      <rect x={x0} y={y0} width={larg} height={alt} fill="var(--accent-soft)" rx="2" />
      {hachuras.map((hx) => (
        <line key={hx} x1={hx} y1={y0 + 5} x2={hx} y2={y1 - 5} stroke="var(--info-border)" strokeWidth="1" />
      ))}
      <line
        x1={cx} y1={y0 + 12} x2={cx} y2={y1 - 12}
        stroke="var(--info-text)" strokeWidth="1.4"
        markerStart="url(#vfSetaCima)" markerEnd="url(#vfSetaBaixo)"
      />
      <defs>
        <marker id="vfSetaCima" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto-start-reverse">
          <path d="M0,8 L4,0 L8,8" fill="none" stroke="var(--info-text)" strokeWidth="1.3" />
        </marker>
        <marker id="vfSetaBaixo" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto-start-reverse">
          <path d="M0,8 L4,0 L8,8" fill="none" stroke="var(--info-text)" strokeWidth="1.3" />
        </marker>
      </defs>
      <text x={cx + 8} y={cy} fill="var(--info-text)" fontSize="10" fontWeight="700" dominantBaseline="middle">
        veio
      </text>

      {/* Cota de LARGURA (horizontal, no topo) */}
      <line x1={x0} y1={yCota} x2={x1} y2={yCota} stroke="var(--ink-2)" strokeWidth="1" />
      <line x1={x0} y1={yCota - 4} x2={x0} y2={yCota + 4} stroke="var(--ink-2)" strokeWidth="1" />
      <line x1={x1} y1={yCota - 4} x2={x1} y2={yCota + 4} stroke="var(--ink-2)" strokeWidth="1" />
      <text x={cx} y={yCota - 6} fill="var(--ink-2)" fontSize="10" fontWeight="600" textAnchor="middle">
        {fmtMedida(l)} mm
      </text>

      {/* Cota de COMPRIMENTO (vertical, à direita) */}
      <line x1={xCota} y1={y0} x2={xCota} y2={y1} stroke="var(--ink-2)" strokeWidth="1" />
      <line x1={xCota - 4} y1={y0} x2={xCota + 4} y2={y0} stroke="var(--ink-2)" strokeWidth="1" />
      <line x1={xCota - 4} y1={y1} x2={xCota + 4} y2={y1} stroke="var(--ink-2)" strokeWidth="1" />
      <text
        x={xCota + 11}
        y={cy}
        fill="var(--ink-2)"
        fontSize="10"
        fontWeight="600"
        textAnchor="middle"
        dominantBaseline="middle"
        transform={`rotate(90 ${xCota + 11} ${cy})`}
      >
        {fmtMedida(c)} mm
      </text>

      {LADOS_PECA.map(({ lado, campo, rotulo }) => {
        const ativa = !!peca?.[campo];
        const { linha, texto } = geomPorLado[lado];
        return (
          <g key={campo}>
            <line
              {...linha}
              strokeLinecap="round"
              stroke={ativa ? "var(--accent)" : "var(--ink-3)"}
              strokeWidth={ativa ? 6 : 2}
              strokeDasharray={ativa ? "none" : "6 5"}
            />
            <text
              x={texto.x}
              y={texto.y}
              fill={ativa ? "var(--accent)" : "var(--ink-2)"}
              fontSize="9.5"
              fontWeight={ativa ? "700" : "500"}
              textAnchor="middle"
              dominantBaseline="middle"
              transform={texto.rot ? `rotate(${texto.rot} ${texto.x} ${texto.y})` : undefined}
            >
              {rotulo}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
